import type React from 'react';
import { useCallback } from 'react';

import Loader from '../Loader';
import {
  FoldoutBtn,
  FoldoutBtnContent,
  FoldoutBtnIcon,
  FoldoutDialog,
  FoldoutDialogHeader,
  FoldoutItem,
  FoldoutList,
  FoldoutUiBlock,
  Foldout as StyledFoldout,
  Wrapper,
} from './wrapper';

export interface IFoldoutItem {
  displayName: string | React.ReactNode;
  title: string;
  tone?: 'error';
  disabled?: boolean;
  isLoading?: boolean;
  icon?: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  testId?: string;
}

export interface IFoldout {
  children: React.ReactNode;
  items: IFoldoutItem[][];
  isOpen?: boolean;
  posX?: 'start' | 'end';
  posXSm?: 'start' | 'end';
  posXMd?: 'start' | 'end';
  posY?: 'top' | 'bottom';
  posYSm?: 'top' | 'bottom';
  posYMd?: 'top' | 'bottom';
  header?: React.ReactNode;
  onClose: (e: React.MouseEvent<HTMLElement>) => void;
}

const Foldout = ({
  children,
  items,
  isOpen,
  posX,
  posXSm,
  posXMd,
  posY,
  posYSm,
  posYMd,
  header,
  onClose,
}: IFoldout) => (
  <Wrapper>
    {children}
    {isOpen && (
      <StyledFoldout
        $posX={posX}
        $posXSm={posXSm}
        $posXMd={posXMd}
        $posY={posY}
        $posYSm={posYSm}
        $posYMd={posYMd}
        tabIndex={0}
      >
        <FoldoutUiBlock onClick={onClose} aria-label="Stäng" aria-hidden="true" />
        <FoldoutDialog>
          {header && <FoldoutDialogHeader>{header}</FoldoutDialogHeader>}
          {items.map((group, i) => (
            <FoldoutList key={`foldout-group-${i}`}>
              {group.map((item, j) => (
                <Item key={`${item.displayName}-${i}-${j}`} item={item} onClick={onClose} />
              ))}
            </FoldoutList>
          ))}
        </FoldoutDialog>
      </StyledFoldout>
    )}
  </Wrapper>
);

export default Foldout;

interface IItemProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  item: IFoldoutItem;
}

const Item = ({ item, onClick }: IItemProps) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick(e);
      item.onClick(e);
    },
    [onClick, item.onClick],
  );

  return (
    <FoldoutItem>
      <FoldoutBtn
        title={item.title}
        onClick={handleClick}
        disabled={item.disabled}
        $tone={item.tone}
        data-testid={item.testId}
      >
        {item.icon && <FoldoutBtnIcon>{item.icon}</FoldoutBtnIcon>}
        <FoldoutBtnContent>{item.displayName}</FoldoutBtnContent>
        {item.isLoading && (
          <FoldoutBtnContent>
            <Loader inlineBlock />
          </FoldoutBtnContent>
        )}
      </FoldoutBtn>
    </FoldoutItem>
  );
};
